/**
 * Open Trivia Database API utilities.
 * https://opentdb.com/api_config.php
 */

const API_BASE = "https://opentdb.com";

/** Shuffle array (Fisher-Yates) */
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Decode HTML entities (e.g. &quot; &#039; &amp;) */
export function decodeHtml(str) {
  if (!str || typeof str !== "string") return "";
  const textarea = document.createElement("textarea");
  textarea.innerHTML = str;
  return textarea.value;
}

/**
 * Fetch trivia questions from Open Trivia Database.
 * @param {Object} options
 * @param {number} [options.amount=10] - Number of questions (max 50)
 * @param {number} [options.category] - Category ID (see api_category.php)
 * @param {string} [options.difficulty] - 'easy' | 'medium' | 'hard'
 * @param {string} [options.type] - 'multiple' | 'boolean'
 * @returns {Promise<{questions: Array, error?: string}>}
 */
export async function fetchTriviaQuestions({ amount = 10, category, difficulty, type } = {}) {
  const params = new URLSearchParams({ amount: Math.min(50, Math.max(1, amount)) });
  if (category) params.set("category", category);
  if (difficulty) params.set("difficulty", difficulty);
  if (type) params.set("type", type);

  try {
    const res = await fetch(`${API_BASE}/api.php?${params.toString()}`);
    const data = await res.json();

    if (data.response_code !== 0) {
      return { questions: [], error: "Could not fetch questions. Try again." };
    }

    const questions = (data.results || []).map((q) => {
      const correct = decodeHtml(q.correct_answer);
      const incorrect = (q.incorrect_answers || []).map(decodeHtml);
      const options = shuffle([correct, ...incorrect]);
      return {
        id: `q-${Math.random().toString(36).slice(2)}`,
        question: decodeHtml(q.question),
        correctAnswer: correct,
        incorrectAnswers: incorrect,
        options,
        category: decodeHtml(q.category),
        difficulty: q.difficulty || "unknown",
        type: q.type || "multiple",
      };
    });

    return { questions };
  } catch (err) {
    console.error("Open Trivia API error:", err);
    return { questions: [], error: "Failed to load questions. Please try again." };
  }
}

/**
 * Fetch all categories from Open Trivia Database.
 * @returns {Promise<Array<{id: number, name: string}>>}
 */
export async function fetchTriviaCategories() {
  try {
    const res = await fetch(`${API_BASE}/api_category.php`);
    const data = await res.json();
    return (data.trivia_categories || []).map((c) => ({ id: c.id, name: decodeHtml(c.name) }));
  } catch (err) {
    console.error("Open Trivia categories error:", err);
    return [];
  }
}
